import { useState } from 'react';
import { useEmbeddingsProviders } from '@/hooks/useEmbeddingsProviders';
import { EmbeddingsProviderDialog } from '@/components/superadmin/EmbeddingsProviderDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  DIMENSION_NOTE, REEMBED_WARNING, activateBlockedReason, canActivate, canDelete, deleteBlockedReason,
  deriveViewState, describeApiError, describeTest, healthText, type EmbeddingsProvider,
} from '@/lib/embeddingsProviderLogic';

const HEALTH_STYLE: Record<string, string> = {
  healthy: 'text-success',
  unhealthy: 'text-destructive',
  unknown: 'text-muted-foreground',
};

type Confirm = { kind: 'activate' | 'deactivate' | 'delete'; provider: EmbeddingsProvider } | null;

export function EmbeddingsSection() {
  const hook = useEmbeddingsProviders();
  const { providers, loading, error, testProvider, setActive, deactivate, deleteProvider, refresh } = hook;
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<EmbeddingsProvider | undefined>();
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [lastConfirm, setLastConfirm] = useState<NonNullable<Confirm> | null>(null);
  const shownConfirm = confirm ?? lastConfirm;
  const anyBusy = busy || rowBusy !== null;
  const askConfirm = (c: NonNullable<Confirm>) => { setLastConfirm(c); setConfirm(c); };
  const view = deriveViewState(providers);

  const fail = (title: string, e: unknown) =>
    toast({ title, description: describeApiError((e as { code?: string }).code, (e as Error).message), variant: 'destructive' });

  const runTest = async (p: EmbeddingsProvider) => {
    setRowBusy(p.id);
    try {
      const r = await testProvider({ id: p.id, baseUrl: p.base_url, defaultModel: p.default_model });
      const d = describeTest(r);
      toast({ title: d.title, description: d.detail, variant: d.ok ? undefined : 'destructive' });
      await refresh({ quiet: true });
    } catch (e) { fail('Test failed', e); } finally { setRowBusy(null); }
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === 'activate') {
        await setActive(confirm.provider.id);
        toast({ title: `${confirm.provider.label} is now the active embeddings provider`, description: REEMBED_WARNING });
      } else if (confirm.kind === 'deactivate') {
        await deactivate(confirm.provider.id);
        toast({ title: 'Embeddings provider deactivated', description: 'Embeddings now use environment variables. ' + REEMBED_WARNING });
      } else {
        await deleteProvider(confirm.provider.id);
        toast({ title: `Deleted ${confirm.provider.label}` });
      }
      setConfirm(null);
    } catch (e) {
      fail(confirm.kind === 'delete' ? 'Could not delete' : confirm.kind === 'activate' ? 'Could not activate' : 'Could not deactivate', e);
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="embeddings-heading" className="space-y-4 border-t pt-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 id="embeddings-heading" className="text-xl font-semibold">Embeddings</h2>
          <p className="text-sm text-muted-foreground">
            Model used to embed documents for search, Ask Masira and Drive ingestion. Separate from the chat provider above.
          </p>
        </div>
        <Button onClick={() => { setEditing(undefined); setDialogOpen(true); }}>Add embeddings provider</Button>
      </div>

      <p className="text-xs text-muted-foreground">{DIMENSION_NOTE}</p>

      {view.banner && !loading && !error && (
        <div role="status" className="rounded-md border border-warning/50 bg-warning/10 p-3 text-sm">{view.banner}</div>
      )}
      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

      {!loading && !error && providers.length === 0 && (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No embeddings providers configured. Add one (for example OpenAI text-embedding-3-small) to manage the key here instead of in env vars.
        </div>
      )}

      {providers.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">Embeddings providers</caption>
            <thead className="text-start text-muted-foreground">
              <tr>
                <th scope="col" className="py-2 text-start">Label</th><th scope="col" className="text-start">Base URL</th>
                <th scope="col" className="text-start">Model</th><th scope="col" className="text-start">Key</th>
                <th scope="col" className="text-start">Health</th><th scope="col" className="text-start">Status</th>
                <th scope="col"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {providers.map((p) => (
                <tr key={p.id} className="border-t">
                  <td className="py-2">{p.label}</td>
                  <td className="font-mono text-xs break-all">{p.base_url}</td>
                  <td className="font-mono text-xs">{p.default_model}</td>
                  <td className="font-mono text-xs">{p.api_key_last4 ? `•••• ${p.api_key_last4}` : '—'}</td>
                  <td className={HEALTH_STYLE[p.health_status]} title={p.health_error ?? ''}>{healthText(p)}</td>
                  <td>
                    {p.is_active
                      ? <span className="rounded bg-success/15 px-2 py-0.5 text-xs font-medium text-success">Active</span>
                      : <span className="text-muted-foreground">inactive</span>}
                  </td>
                  <td className="space-x-3 text-end whitespace-nowrap">
                    {p.is_active ? (
                      <button className="text-primary underline disabled:opacity-50 disabled:no-underline" disabled={anyBusy}
                        aria-label={`Deactivate ${p.label}`}
                        onClick={() => askConfirm({ kind: 'deactivate', provider: p })}>deactivate</button>
                    ) : (
                      <>
                        <button className="text-primary underline disabled:opacity-50 disabled:no-underline"
                          disabled={anyBusy || !canActivate(p)} aria-label={`Activate ${p.label}`}
                          aria-describedby={activateBlockedReason(p) ? `${p.id}-activate-reason` : undefined}
                          title={activateBlockedReason(p) ?? undefined}
                          onClick={() => askConfirm({ kind: 'activate', provider: p })}>activate</button>
                        {activateBlockedReason(p) && <span id={`${p.id}-activate-reason`} className="sr-only">{activateBlockedReason(p)}</span>}
                      </>
                    )}
                    <button className="text-primary underline disabled:opacity-50 disabled:no-underline" disabled={anyBusy}
                      aria-label={`Test ${p.label}`} onClick={() => void runTest(p)}>
                      {rowBusy === p.id ? 'testing…' : 'test'}
                    </button>
                    <button className="text-primary underline disabled:opacity-50 disabled:no-underline" disabled={anyBusy}
                      aria-label={`Edit ${p.label}`} onClick={() => { setEditing(p); setDialogOpen(true); }}>edit</button>
                    <button className="text-destructive underline disabled:opacity-50 disabled:no-underline"
                      disabled={anyBusy || !canDelete(p)} aria-label={`Delete ${p.label}`}
                      aria-describedby={deleteBlockedReason(p) ? `${p.id}-delete-reason` : undefined}
                      title={deleteBlockedReason(p) ?? undefined}
                      onClick={() => askConfirm({ kind: 'delete', provider: p })}>delete</button>
                    {deleteBlockedReason(p) && <span id={`${p.id}-delete-reason`} className="sr-only">{deleteBlockedReason(p)}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EmbeddingsProviderDialog open={dialogOpen} onOpenChange={setDialogOpen} provider={editing} actions={hook} />

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o && !busy) setConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {shownConfirm?.kind === 'activate' && `Activate ${shownConfirm.provider.label}?`}
              {shownConfirm?.kind === 'deactivate' && `Deactivate ${shownConfirm.provider.label}?`}
              {shownConfirm?.kind === 'delete' && `Delete ${shownConfirm.provider.label}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {shownConfirm?.kind === 'activate' && `${REEMBED_WARNING} Model: ${shownConfirm.provider.default_model}.`}
              {shownConfirm?.kind === 'deactivate' && `Embeddings will fall back to the environment variables. ${REEMBED_WARNING}`}
              {shownConfirm?.kind === 'delete' && 'This removes the provider and its stored key. This cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={(e) => { e.preventDefault(); void runConfirm(); }}>
              {shownConfirm?.kind === 'activate' ? 'Activate' : shownConfirm?.kind === 'deactivate' ? 'Deactivate' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
